import { createSelector } from "@reduxjs/toolkit";
import { orderBy } from "lodash";
import { RootState } from "@store/index";
import { DataRoomState, EntityDocumentGridRow, EntityDocumentType } from "@dataRooms/models";
import { Nullable } from "@shared/models/general";
import { entitledFundsSelector } from "@store/Entities/selectors";
import { FundraisingStatus, ProductLine } from "@store/Entities/models";
import DateUtils from "@shared/utils/DateUtils";
import { DocumentStatus, isDocumentPendingOrApproved } from "@documents/models/filter.models";
import { preferencesSelector } from "@app/store/selectors";
import { HomePageVideoDetails } from "@home/models";
import FeatureToggleService from "@services/FeatureToggleService";
import { ToggledFeature } from "@shared/models/featureToggle";
import { SegmentTab, segmentTabValues } from "@dataRooms/constants/SegmentTabs";
import {
  documentTypeFallbackNames,
  documentTypeMap,
  documentTypeMapMerge,
  fundSummaryGroup,
} from "@dataRooms/constants";
import { DataGridAsyncSlice } from "@shared/models/redux";
import { DocumentRow } from "@documents/models/dataGrid.models";
import { buildEntityDocumentRowMeta } from "@dataRooms/utils/buildEntityDocumentRowMeta";

export const entityDocumentTypesSelector = (state: RootState): DataRoomState["entityDocumentTypes"] =>
  state.dataRooms.entityDocumentTypes;

export const entityDocumentsSelector = (state: RootState): DataRoomState["entityDocuments"] =>
  state.dataRooms.entityDocuments;

export const highlightedVideoSelector = (state: RootState): DataRoomState["highlightedVideo"] =>
  state.dataRooms.highlightedVideo;

export const sortSelector = (state: RootState): DataRoomState["sort"] => state.dataRooms.sort;

export const searchKeySelector = (state: RootState): DataRoomState["searchKey"] => state.dataRooms.searchKey;

export const activeSegmentTabSelector = (state: RootState): DataRoomState["activeSegmentTab"] =>
  state.dataRooms.activeSegmentTab;

export const highlightedVideoSectionDetailsSelector = createSelector(
  highlightedVideoSelector,
  (highlightedVideo): Nullable<HomePageVideoDetails> => {
    const isBrightcoveEnabled = FeatureToggleService.isFeatureEnabled(ToggledFeature.BrightcoveVideo);

    if (!highlightedVideo || highlightedVideo.videoId === null) {
      return null;
    }

    if (
      !isBrightcoveEnabled &&
      (highlightedVideo.videoTokenExpiration === null || highlightedVideo.videoToken === null)
    ) {
      return null;
    }

    return {
      id: highlightedVideo.videoId,
      title: highlightedVideo.title,
      type: "New Opportunity Spotlight",
      tokenExpiration: highlightedVideo.videoTokenExpiration ?? "",
      token: highlightedVideo.videoToken ?? "",
      description: highlightedVideo.videoDescription,
    };
  }
);

export const dataRoomsFromEntitledFundsSelector = createSelector(
  entitledFundsSelector,
  ({
    data: entitledFunds,
  }): {
    id: number;
    globalId: string;
    title: string;
    subTitle: Nullable<string>;
    productSegment: string;
  }[] => {
    if (entitledFunds === null) {
      return [];
    }

    return Object.values(entitledFunds)
      .filter(
        (entitledFund) =>
          entitledFund.fundraisingStatus === FundraisingStatus.Fundraising &&
          entitledFund.productLine !== ProductLine.NonInvestment &&
          entitledFund.productFamily !== "Investor Conferences"
      )
      .map(({ id, globalId, entityDisplayName, entityName, productFamily, productSegment }) => ({
        id,
        globalId,
        title: entityDisplayName ?? entityName,
        subTitle: productFamily,
        productSegment,
      }));
  }
);

export const dataRoomsIncludeClosedStatusSelector = createSelector(
  entitledFundsSelector,
  ({
    data: entitledFunds,
  }): {
    id: number;
    globalId: string;
    title: string;
    subTitle: Nullable<string>;
    productSegment: string;
  }[] => {
    if (entitledFunds === null) {
      return [];
    }

    return Object.values(entitledFunds)
      .filter(
        (entitledFund) =>
          (entitledFund.fundraisingStatus === FundraisingStatus.Fundraising ||
            entitledFund.fundraisingStatus === FundraisingStatus.Closed) &&
          entitledFund.productLine !== ProductLine.NonInvestment &&
          entitledFund.productFamily !== "Investor Conferences"
      )
      .map(({ id, globalId, entityDisplayName, entityName, productFamily, productSegment }) => ({
        id,
        globalId,
        title: entityDisplayName ?? entityName,
        subTitle: productFamily,
        productSegment,
      }));
  }
);

export const dataRoomsBySegmentSelector = createSelector(
  dataRoomsFromEntitledFundsSelector,
  activeSegmentTabSelector,
  (dataRooms, activeSegmentTab) => {
    if (!dataRooms.length) {
      return [];
    }

    if (activeSegmentTab === SegmentTab.ViewAll) {
      return dataRooms;
    }

    return dataRooms.filter((dataRoom) => dataRoom.productSegment === segmentTabValues[activeSegmentTab]);
  }
);

export const dataRoomsSelector = createSelector(
  dataRoomsBySegmentSelector,
  sortSelector,
  searchKeySelector,
  (
    dataRoomsBySegment,
    sort,
    searchKey
  ): {
    id: number;
    globalId: string;
    title: string;
    subTitle: Nullable<string>;
  }[] => {
    if (!dataRoomsBySegment.length) {
      return [];
    }
    const dataRoomsBySearchKey = dataRoomsBySegment.filter((dataRoom) => {
      return dataRoom.title.toLowerCase().includes(searchKey.toLowerCase());
    });

    return orderBy(
      dataRoomsBySearchKey,
      [({ title }) => title.toLowerCase()],
      sort.direction.toLowerCase() as "asc" | "desc"
    );
  }
);

export const populatedEntityDocumentTypesSelector = createSelector(
  [entityDocumentTypesSelector, entityDocumentsSelector],
  ({ data: documentTypes }, { data: documents }) => {
    if (!documentTypes || !documents) return [];
    const documentTypeMapwithID = new Map(documentTypes.map((d) => [d.documentTypeId, d]));

    const finalList: EntityDocumentType[] = [];
    const addedIds = new Set<number>();

    for (const [publicStr, privateList] of Object.entries(documentTypeMap)) {
      const publicId = Number(publicStr);
      const publicDoc = documents[publicId];
      const publicMeta = documentTypeMapwithID.get(publicId);

      const publicHasDocs = publicDoc?.data ? publicDoc?.data.length > 0 : false;

      if (publicHasDocs && publicMeta && !addedIds.has(publicId)) {
        finalList.push(publicMeta);
        addedIds.add(publicId);
      } else {
        const firstPrivateWithDocs = privateList.find((privateId) => documents?.[privateId]?.data);

        if (firstPrivateWithDocs && !addedIds.has(firstPrivateWithDocs)) {
          const privateMeta = documentTypeMapwithID.get(firstPrivateWithDocs);

          if (privateMeta) {
            finalList.push({
              ...privateMeta,
              name: publicMeta?.name ?? documentTypeFallbackNames[publicId] ?? `Documents - ${publicId}`,
            });
            addedIds.add(firstPrivateWithDocs);
          }
        }
      }
    }
    const allPrivateIds = new Set(Object.values(documentTypeMap).flat());
    const mappedPublicIds = new Set(Object.keys(documentTypeMap).map(Number));

    for (const docType of documentTypes) {
      const { documentTypeId } = docType;
      const isMapped = mappedPublicIds.has(documentTypeId);
      const isPrivate = allPrivateIds.has(documentTypeId);
      const hasDocs = documents?.[documentTypeId]?.data;

      if (!isMapped && !isPrivate && hasDocs && !addedIds.has(documentTypeId)) {
        finalList.push(docType);
        addedIds.add(documentTypeId);
      }
    }

    return finalList.sort((a, b) => {
      const isA_FundSummary = fundSummaryGroup.has(a.documentTypeId);
      const isB_FundSummary = fundSummaryGroup.has(b.documentTypeId);

      if (isA_FundSummary && !isB_FundSummary) return -1;
      if (!isA_FundSummary && isB_FundSummary) return 1;

      return 0;
    });
  }
);

export const populatedEntityAllDocumentTypesSelector = createSelector(
  entityDocumentTypesSelector,
  entityDocumentsSelector,
  ({ data: documentTypes }, { data: documents }): Nullable<EntityDocumentType[]> => {
    if (documentTypes === null || documents === null) {
      return null;
    }

    return documentTypes.filter(({ documentTypeId }) => documents[documentTypeId]);
  }
);

export const entityDocumentsGridSelector = createSelector(
  [entityDocumentsSelector, (_state: RootState, typeId: number) => typeId, preferencesSelector],
  ({ data: documents }, typeId, preferences): EntityDocumentGridRow[] => {
    const slice = documents?.[typeId];

    if (!slice || !slice.data) {
      return [];
    }

    // Exclude highlighted videos from the DataRooms datagrid; these are surfaced
    // separately via the highlighted video section on the profile page.
    const source = slice.data.filter(
      ({ videoId, videoIsHighlighted }) => !(videoId !== null && videoId !== undefined && videoIsHighlighted)
    );

    return source.map(
      ({
        actionItem,
        title,
        publishDate,
        videoId,
        videoToken,
        documentStatus,
        markedRead,
        fileType,
        id,
        documentSubtype,
        documentTypeId,
        fundId,
        batchId,
        otherAccounts,
        isLoading,
        readOnlyFlag,
      }: DocumentRow) => {
        const isPendingOrApproved = isDocumentPendingOrApproved(documentStatus);
        const isVideo = videoId !== null;

        const row = {
          actionItem,
          title,
          publishDate:
            documentStatus === DocumentStatus.Active && publishDate
              ? DateUtils.formatDate(new Date(publishDate), preferences.DateFormat)
              : "N/A",
          markedRead,
          id,
          fileType,
          documentSubtype,
          fundId,
          documentTypeId,
          batchId,
          otherAccounts,
          isLoading,
          readOnlyFlag,
        };

        const rowMeta = buildEntityDocumentRowMeta({ isVideo, readOnlyFlag, fileType, isPendingOrApproved });

        return isVideo
          ? {
              ...row,
              publishDate: publishDate ? DateUtils.formatDate(new Date(publishDate), preferences.DateFormat) : "N/A",
              videoId,
              videoToken,
              rowMeta,
            }
          : {
              ...row,
              rowMeta,
            };
      }
    );
  }
);

export const makeEntityDocumentGridSelector = (typeId: string) =>
  createSelector(
    [
      (state: RootState): DataGridAsyncSlice<DocumentRow[]> | undefined => {
        const entityDocs = entityDocumentsSelector(state);

        return entityDocs?.data?.[typeId];
      },
      preferencesSelector,
    ],
    (documentForType, preferences) => {
      if (!documentForType?.data) return [];

      // Exclude highlighted videos from the DataRooms datagrid; these are
      // rendered in the separate highlighted video section instead.
      const source = documentForType.data.filter(
        ({ videoId, videoIsHighlighted }) => !(videoId !== null && videoId !== undefined && videoIsHighlighted)
      );

      return (
        source.map(
          ({
            actionItem,
            title,
            publishDate,
            videoId,
            videoToken,
            documentStatus,
            markedRead,
            fileType,
            id,
            documentSubtype,
            documentTypeId,
            fundId,
            batchId,
            otherAccounts,
            isLoading,
            readOnlyFlag,
          }) => {
            const isPendingOrApproved = isDocumentPendingOrApproved(documentStatus);
            const isVideo = videoId !== null;

            const row = {
              actionItem,
              title,
              publishDate:
                documentStatus === DocumentStatus.Active && publishDate
                  ? DateUtils.formatDate(new Date(publishDate), preferences.DateFormat)
                  : "N/A",
              markedRead,
              id,
              fileType,
              documentSubtype,
              fundId,
              documentTypeId,
              batchId,
              otherAccounts,
              isLoading,
              readOnlyFlag,
            };

            const rowMeta = buildEntityDocumentRowMeta({ isVideo, readOnlyFlag, fileType, isPendingOrApproved });

            return isVideo
              ? {
                  ...row,
                  publishDate: publishDate
                    ? DateUtils.formatDate(new Date(publishDate), preferences.DateFormat)
                    : "N/A",
                  videoId,
                  videoToken,
                  rowMeta,
                }
              : {
                  ...row,
                  rowMeta,
                };
          }
        ) ?? []
      );
    }
  );

export const makesEntityAllDocumentsGridSelector = (typeId: string) => {
  const typeIdsToUse = documentTypeMapMerge[typeId] ?? [typeId];

  const documentSelectors = typeIdsToUse.map((id) => makeEntityDocumentGridSelector(id));

  const sortSelector = (state: RootState) =>
    state.dataRooms.entityDocuments.data?.[typeId]?.sort ?? {
      key: "publishDate",
      direction: "DESC",
    };

  return createSelector(
    [sortSelector, ...documentSelectors],
    (
      sort: { key: string | null; direction: "ASC" | "DESC" },
      ...allDocsArrays: EntityDocumentGridRow[][]
    ): EntityDocumentGridRow[] => {
      const mergedDocs = allDocsArrays.flat();

      if (typeIdsToUse.length <= 1) {
        return mergedDocs;
      }

      const { key, direction } = sort;

      return [...mergedDocs].sort((a, b) => {
        //@ts-ignore
        if (!a.fileType || !b.fileType) return 0;
        //@ts-ignore
        let aVal = a[key];
        //@ts-ignore
        let bVal = b[key];

        if (key === "publishDate") {
          const parseDate = (val: string) => {
            if (val === "N/A" || !val) return null;
            const time = new Date(val.replace(/\s*-\s*/g, "-")).getTime();

            return isNaN(time) ? null : time;
          };

          aVal = parseDate(aVal);
          bVal = parseDate(bVal);
        }

        const isAscending = direction === "ASC";

        if (aVal === null && bVal === null) return 0;
        if (aVal === null) return -1;
        if (bVal === null) return 1;

        if (key === "title") {
          aVal = aVal.toLowerCase();
          bVal = bVal.toLowerCase();

          return isAscending ? (aVal > bVal ? 1 : aVal < bVal ? -1 : 0) : aVal < bVal ? 1 : aVal > bVal ? -1 : 0;
        }

        return isAscending ? (aVal > bVal ? 1 : -1) : aVal < bVal ? 1 : -1;
      });
    }
  );
};
